import { useState, useEffect, type ReactNode, type FormEvent } from 'react';
import { useContentStore, type ContentPiece, type CreateContentInput, type UpdateContentInput } from '../stores/content';
import { ContentGenerator } from '../components/ContentGenerator';

// =============================================================================
// Create/Edit Modal Component
// =============================================================================

interface ContentModalProps {
  isOpen: boolean;
  onClose: () => void;
  content?: ContentPiece | null;
  onSubmit: (data: CreateContentInput | UpdateContentInput) => Promise<void>;
  isLoading: boolean;
}

function ContentModal({ isOpen, onClose, content, onSubmit, isLoading }: ContentModalProps): ReactNode {
  const [title, setTitle] = useState('');
  const [type, setType] = useState<ContentPiece['type']>('article');
  const [primaryKeyword, setPrimaryKeyword] = useState('');
  const [contentBody, setContentBody] = useState('');
  const [status, setStatus] = useState<ContentPiece['status']>('draft');

  const isEditing = content !== null && content !== undefined;

  useEffect(() => {
    if (content) {
      setTitle(content.title);
      setType(content.type);
      setPrimaryKeyword(content.primaryKeyword ?? '');
      setContentBody(content.content ?? '');
      setStatus(content.status);
    } else {
      setTitle('');
      setType('article');
      setPrimaryKeyword('');
      setContentBody('');
      setStatus('draft');
    }
  }, [content, isOpen]);

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();

    if (isEditing) {
      const data: UpdateContentInput = { title, status };
      if (contentBody) data.content = contentBody;
      if (primaryKeyword) data.primaryKeyword = primaryKeyword;
      await onSubmit(data);
    } else {
      const data: CreateContentInput = { title, type };
      if (contentBody) data.content = contentBody;
      if (primaryKeyword) data.primaryKeyword = primaryKeyword;
      await onSubmit(data);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />

        <div className="relative w-full max-w-2xl rounded-lg bg-white p-6 shadow-xl">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">
              {isEditing ? 'Edit Content' : 'Create New Content'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                Title
              </label>
              <input
                type="text"
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                placeholder="Enter content title..."
              />
            </div>

            {!isEditing && (
              <div>
                <label htmlFor="type" className="block text-sm font-medium text-gray-700">
                  Content Type
                </label>
                <select
                  id="type"
                  value={type}
                  onChange={(e) => setType(e.target.value as ContentPiece['type'])}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                >
                  <option value="article">Article</option>
                  <option value="review">Review</option>
                  <option value="comparison">Comparison</option>
                  <option value="listicle">Listicle</option>
                  <option value="guide">Guide</option>
                </select>
              </div>
            )}

            <div>
              <label htmlFor="primaryKeyword" className="block text-sm font-medium text-gray-700">
                Primary Keyword
              </label>
              <input
                type="text"
                id="primaryKeyword"
                value={primaryKeyword}
                onChange={(e) => setPrimaryKeyword(e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                placeholder="Target SEO keyword..."
              />
            </div>

            <div>
              <label htmlFor="content" className="block text-sm font-medium text-gray-700">
                Content
              </label>
              <textarea
                id="content"
                value={contentBody}
                onChange={(e) => setContentBody(e.target.value)}
                rows={8}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                placeholder="Write your content here..."
              />
            </div>

            {isEditing && (
              <div>
                <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                  Status
                </label>
                <select
                  id="status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value as ContentPiece['status'])}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                >
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading || !title}
                className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
              >
                {isLoading ? 'Saving...' : isEditing ? 'Save Changes' : 'Create Content'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Delete Confirmation Modal
// =============================================================================

interface DeleteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  contentTitle: string;
  isLoading: boolean;
}

function DeleteModal({ isOpen, onClose, onConfirm, contentTitle, isLoading }: DeleteModalProps): ReactNode {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />

        <div className="relative w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
          <h3 className="text-lg font-semibold text-gray-900">Delete Content</h3>
          <p className="mt-2 text-sm text-gray-500">
            Are you sure you want to delete &quot;{contentTitle}&quot;? This action cannot be undone.
          </p>

          <div className="mt-6 flex justify-end gap-3">
            <button
              onClick={onClose}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={() => void onConfirm()}
              disabled={isLoading}
              className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
            >
              {isLoading ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Main Content Page
// =============================================================================

export function ContentPage(): ReactNode {
  const {
    content,
    isLoading,
    error,
    fetchContent,
    createContent,
    updateContent,
    deleteContent,
    clearError,
  } = useContentStore();

  const [filter, setFilter] = useState<'all' | 'published' | 'draft'>('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isGeneratorOpen, setIsGeneratorOpen] = useState(false);
  const [editingContent, setEditingContent] = useState<ContentPiece | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ContentPiece | null>(null);

  useEffect(() => {
    if (filter === 'all') {
      void fetchContent({});
    } else {
      void fetchContent({ status: filter });
    }
  }, [filter, fetchContent]);

  const handleCreate = (): void => {
    setEditingContent(null);
    setIsModalOpen(true);
  };

  const handleEdit = (item: ContentPiece): void => {
    setEditingContent(item);
    setIsModalOpen(true);
  };

  const handleModalSubmit = async (data: CreateContentInput | UpdateContentInput): Promise<void> => {
    if (editingContent) {
      const result = await updateContent(editingContent.id, data as UpdateContentInput);
      if (result) {
        setIsModalOpen(false);
        setEditingContent(null);
      }
    } else {
      const result = await createContent(data as CreateContentInput);
      if (result) {
        setIsModalOpen(false);
      }
    }
  };

  const handleDeleteConfirm = async (): Promise<void> => {
    if (deleteTarget) {
      const success = await deleteContent(deleteTarget.id);
      if (success) {
        setDeleteTarget(null);
      }
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Content Engine</h2>
          <p className="text-gray-500">
            AI-powered content with automatic decay detection
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setIsGeneratorOpen(true)}
            className="rounded-md border border-primary-600 px-4 py-2 text-sm font-medium text-primary-600 hover:bg-primary-50"
          >
            🤖 AI Generate
          </button>
          <button
            onClick={handleCreate}
            className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
          >
            + New Content
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="rounded-lg bg-red-50 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
            <div className="ml-auto pl-3">
              <button onClick={clearError} className="text-red-500 hover:text-red-600">
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-2">
        {(['all', 'published', 'draft'] as const).map((f) => (
          <button
            key={f}
            onClick={() => { setFilter(f); }}
            className={`rounded-md px-4 py-2 text-sm font-medium ${
              filter === f
                ? 'bg-primary-100 text-primary-700'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {/* Content List */}
      {isLoading && content.length === 0 ? (
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="animate-pulse rounded-lg border bg-white p-6">
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
              <div className="h-3 bg-gray-200 rounded w-1/2"></div>
            </div>
          ))}
        </div>
      ) : content.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-12 text-center">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <h3 className="mt-4 text-lg font-medium text-gray-900">No content yet</h3>
          <p className="mt-2 text-gray-500">Get started by creating your first piece of content.</p>
          <button
            onClick={handleCreate}
            className="mt-4 rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
          >
            Create Content
          </button>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border bg-white">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Title
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Health
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Views (7d)
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {content.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{item.title}</div>
                    <div className="text-sm text-gray-500">/{item.slug}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      {item.type}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      item.status === 'published' ? 'bg-green-100 text-green-800' :
                      item.status === 'draft' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {item.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      item.decayStatus === 'healthy' ? 'bg-green-100 text-green-800' :
                      item.decayStatus === 'warning' ? 'bg-yellow-100 text-yellow-800' :
                      item.decayStatus === 'critical' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {item.decayStatus}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {item.pageViews7d.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleEdit(item)}
                      className="text-primary-600 hover:text-primary-900 mr-4"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => setDeleteTarget(item)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modals */}
      <ContentModal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setEditingContent(null); }}
        content={editingContent}
        onSubmit={handleModalSubmit}
        isLoading={isLoading}
      />

      <DeleteModal
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        contentTitle={deleteTarget?.title ?? ''}
        isLoading={isLoading}
      />

      <ContentGenerator
        isOpen={isGeneratorOpen}
        onClose={() => setIsGeneratorOpen(false)}
        onContentGenerated={(content) => {
          // Auto-create content piece with generated content
          void createContent({
            title: 'AI Generated Content',
            type: 'article',
            content,
          });
        }}
      />
    </div>
  );
}
